/**
 * @file BenefitPlanOverviewTab.js
 * @description Tab component for managing basic benefit plan information.
 */

import React, {
    useState,
    useEffect,
    forwardRef,
    useImperativeHandle,
} from 'react';
import { CharacterLimitIndicator } from '../../helpers/CharacterLimitIndicator';
import PlanTypeDropdown from '../../shared/asyncDropdowns/PlanTypeDropdown';
import VendorDropdown from '../../shared/asyncDropdowns/VendorDropdown';
import NetworkTypeDropdown from '../../shared/asyncDropdowns/NetworkTypeDropdown';
import SSODropdown from '../../shared/asyncDropdowns/SSODropdown';
import { isValidPhoneNumber, validateUrl } from '../../../utils/validate';
import PhoneInput from 'react-phone-number-input/input';
import Toast from '../../ui/toasts/Toast';
import Tooltip from '../../ui/tooltips/Tooltip';
import { getText } from '../../../i18n';

const BenefitPlanOverviewTab = forwardRef(
    ({ data, onChange, highlightedField, onFieldFocus, onSave }, ref) => {
        /**
         * Normalizes a phone number to E.164 format for the PhoneInput component
         * @param {string} phoneNumber - The phone number to normalize
         * @returns {string|null} - The normalized phone number or null
         */
        const normalizePhoneNumberForInput = phoneNumber => {
            if (!phoneNumber) return null;
            const trimmedNumber = phoneNumber.trim();
            if (!trimmedNumber) return null;

            if (trimmedNumber.startsWith('+')) {
                return trimmedNumber;
            }

            const digitsOnly = trimmedNumber.replace(/\D/g, '');

            if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
                return `+${digitsOnly}`;
            }

            if (digitsOnly.length === 10) {
                return `+1${digitsOnly}`;
            }

            if (digitsOnly.startsWith('1')) {
                return `+${digitsOnly}`;
            }

            return null;
        };

        const [formData, setFormData] = useState({
            code: data.code || '',
            name: data.name || '',
            planTypeId: data.planTypeId || null,
            networkTypeId: data.networkTypeId || null,
            networkName: data.networkName || '',
            vendorId: data.vendorId || null,
            policy: data.policy || '',
            phoneNumber: normalizePhoneNumberForInput(data.phoneNumber),
            tenantSSOId: data.tenantSSOId || null,
            url: data.url || '',
            providerList: data.providerList || '',
            enabled: data.enabled !== undefined ? data.enabled : true,
            overrideName: data.overrideName || '',
        });

        const [errors, setErrors] = useState({
            code: '',
            name: '',
            planTypeId: '',
            vendorId: '',
            url: '',
            providerList: '',
            phoneNumber: '',
        });

        const [showUrlErrors, setShowUrlErrors] = useState(false);

        /**
         * Validates the format of a phone number
         * @param {string} phoneNumber - The phone number to validate.
         * @returns {string} - An error message if invalid, otherwise an empty string.
         */
        const validatePhoneNumber = phoneNumber => {
            if (!phoneNumber) return '';
            return isValidPhoneNumber(phoneNumber)
                ? ''
                : 'Invalid phone number.';
        };

        const isFieldHighlighted = fieldName => {
            return highlightedField === fieldName;
        };

        const getFieldClasses = (fieldName, hasError = false) => {
            let classes = 'form-control';
            if (hasError) {
                classes += ' is-invalid';
            }
            if (isFieldHighlighted(fieldName)) {
                classes += ' is-invalid border-danger';
            }
            return classes;
        };

        const handleFieldFocus = () => {
            if (onFieldFocus) {
                onFieldFocus();
            }
        };

        useEffect(() => {
            setFormData({
                code: data.code || '',
                name: data.name || '',
                planTypeId: data.planTypeId || null,
                networkTypeId: data.networkTypeId || null,
                networkName: data.networkName || '',
                vendorId: data.vendorId || null,
                policy: data.policy || '',
                phoneNumber: normalizePhoneNumberForInput(data.phoneNumber),
                tenantSSOId: data.tenantSSOId || null,
                url: data.url || '',
                providerList: data.providerList || '',
                enabled: data.enabled !== undefined ? data.enabled : true,
                overrideName: data.overrideName || '',
            });
        }, [data]);

        useEffect(() => {
            if (
                highlightedField === 'url' ||
                highlightedField === 'providerList'
            ) {
                setShowUrlErrors(true);

                if (highlightedField === 'url' && formData.url.trim()) {
                    const urlValidation = validateUrl(formData.url.trim());
                    if (!urlValidation.isValid) {
                        setErrors(prevErrors => ({
                            ...prevErrors,
                            url: urlValidation.error,
                        }));
                    }
                }

                if (
                    highlightedField === 'providerList' &&
                    formData.providerList.trim()
                ) {
                    const providerListValidation = validateUrl(
                        formData.providerList.trim(),
                    );
                    if (!providerListValidation.isValid) {
                        setErrors(prevErrors => ({
                            ...prevErrors,
                            providerList: providerListValidation.error,
                        }));
                    }
                }
            }
        }, [highlightedField, formData.url, formData.providerList]);

        useEffect(() => {
            if (
                highlightedField &&
                ['name', 'code', 'planTypeId', 'vendorId'].includes(
                    highlightedField,
                )
            ) {
                const newErrors = {
                    code: '',
                    name: '',
                    planTypeId: '',
                    vendorId: '',
                    url: '',
                    providerList: '',
                    phoneNumber: '',
                };

                const fieldsToValidate = [
                    { name: 'code', value: formData.code },
                    { name: 'name', value: formData.name },
                    { name: 'planTypeId', value: formData.planTypeId },
                    { name: 'vendorId', value: formData.vendorId },
                    { name: 'phoneNumber', value: formData.phoneNumber },
                    { name: 'url', value: formData.url },
                    { name: 'providerList', value: formData.providerList },
                ];

                fieldsToValidate.forEach(field => {
                    const errorMessage = validateField(field.name, field.value);
                    if (errorMessage) {
                        newErrors[field.name] = errorMessage;
                    }
                });

                setErrors(newErrors);
                setShowUrlErrors(true);
            }
        }, [highlightedField, formData]);

        const validateField = (name, value) => {
            let errorMessage = '';

            switch (name) {
                case 'code':
                    if (!value.trim()) {
                        errorMessage = 'Plan code is required';
                    } else if (value.length > 128) {
                        errorMessage = 'Plan code cannot exceed 128 characters';
                    }
                    break;

                case 'name':
                    if (!value.trim()) {
                        errorMessage = 'Plan name is required';
                    } else if (value.length > 100) {
                        errorMessage = 'Plan name cannot exceed 100 characters';
                    }
                    break;

                case 'planTypeId':
                    if (!value) {
                        errorMessage = 'Plan type is required';
                    }
                    break;

                case 'vendorId':
                    if (!value) {
                        errorMessage = 'Carrier is required';
                    }
                    break;

                case 'phoneNumber':
                    if (value && `${value}`.trim()) {
                        errorMessage = validatePhoneNumber(value);
                    }
                    break;

                case 'url':
                    if (value.trim()) {
                        const urlValidation = validateUrl(value.trim());
                        if (!urlValidation.isValid) {
                            errorMessage = urlValidation.error;
                        }
                    }
                    break;

                case 'providerList':
                    if (value.trim()) {
                        const urlValidation = validateUrl(value.trim());
                        if (!urlValidation.isValid) {
                            errorMessage = urlValidation.error;
                        }
                    }
                    break;

                default:
                    break;
            }

            return errorMessage;
        };

        /**
         * Validates all form fields and sets errors for invalid fields
         * @returns {boolean} - Returns true if all fields are valid, false otherwise
         */
        const validateAllFields = () => {
            const newErrors = {
                code: '',
                name: '',
                planTypeId: '',
                vendorId: '',
                url: '',
                providerList: '',
                phoneNumber: '',
            };

            let hasErrors = false;

            const fieldsToValidate = [
                { name: 'code', value: formData.code },
                { name: 'name', value: formData.name },
                { name: 'planTypeId', value: formData.planTypeId },
                { name: 'vendorId', value: formData.vendorId },
                { name: 'phoneNumber', value: formData.phoneNumber },
                { name: 'url', value: formData.url },
                { name: 'providerList', value: formData.providerList },
            ];

            fieldsToValidate.forEach(field => {
                const errorMessage = validateField(field.name, field.value);
                if (errorMessage) {
                    newErrors[field.name] = errorMessage;
                    hasErrors = true;
                }
            });

            setErrors(newErrors);
            setShowUrlErrors(true);

            if (hasErrors) {
                const errorCount = Object.values(newErrors).filter(
                    error => error.trim() !== '',
                ).length;

                if (errorCount === 1) {
                    const singleError = Object.values(newErrors).find(
                        error => error.trim() !== '',
                    );
                    Toast.error(singleError);
                } else {
                    Toast.error(getText('msg_e0030e1601dc'));
                }
                return false;
            }

            return true;
        };

        useImperativeHandle(ref, () => ({
            validateAllFields,
        }));

        const handleChange = e => {
            const { name, value, type, checked } = e.target;
            const fieldValue = type === 'checkbox' ? checked : value;

            let errorMessage = '';
            if (name !== 'url' && name !== 'providerList') {
                errorMessage = validateField(name, fieldValue);
            }

            setErrors(prevErrors => ({
                ...prevErrors,
                [name]: errorMessage,
            }));

            setFormData(prevData => {
                const newData = {
                    ...prevData,
                    [name]: fieldValue,
                };
                onChange(newData);
                return newData;
            });
        };

        const handlePhoneChange = value => {
            const errorMessage = validateField('phoneNumber', value);

            setErrors(prevErrors => ({
                ...prevErrors,
                phoneNumber: errorMessage,
            }));

            setFormData(prevData => {
                const newData = {
                    ...prevData,
                    phoneNumber: value || null,
                };
                onChange(newData);
                return newData;
            });
        };

        const handlePlanTypeChange = planTypeId => {
            const errorMessage = validateField('planTypeId', planTypeId);

            setErrors(prevErrors => ({
                ...prevErrors,
                planTypeId: errorMessage,
            }));

            setFormData(prevData => {
                const newData = {
                    ...prevData,
                    planTypeId,
                };
                onChange(newData);
                return newData;
            });
        };

        const handleVendorChange = vendorId => {
            const errorMessage = validateField('vendorId', vendorId);

            setErrors(prevErrors => ({
                ...prevErrors,
                vendorId: errorMessage,
            }));

            setFormData(prevData => {
                const newData = {
                    ...prevData,
                    vendorId,
                };
                onChange(newData);
                return newData;
            });
        };

        const handleNetworkTypeChange = networkTypeId => {
            setFormData(prevData => {
                const newData = {
                    ...prevData,
                    networkTypeId,
                };
                onChange(newData);
                return newData;
            });
        };

        const handleSSOChange = tenantSSOId => {
            setFormData(prevData => {
                const newData = {
                    ...prevData,
                    tenantSSOId,
                };
                onChange(newData);
                return newData;
            });
        };

        return (
            <div className="col-12 col-xs-12 col-md-12 col-lg-10 m-auto pb-5 pt-4">
                <div className="row">
                    <div className="col-12 col-md-6 pe-md-3 d-flex align-items-start align-self-baseline">
                        <div
                            className="form-group mb-3 w-100"
                            style={{ minHeight: '85px' }}
                        >
                            <label htmlFor="code">
                                {getText('msg_ce8a5e80ab2a')}{' '}
                                <span className="text-danger">*</span>
                            </label>
                            <div className="position-relative">
                                <input
                                    type="text"
                                    id="code"
                                    name="code"
                                    className={getFieldClasses(
                                        'code',
                                        errors.code,
                                    )}
                                    value={formData.code}
                                    onChange={handleChange}
                                    onFocus={handleFieldFocus}
                                    required
                                    placeholder={getText('msg_c59e567012a4')}
                                    maxLength={128}
                                    tabIndex={1}
                                />
                                <CharacterLimitIndicator
                                    inputStr={formData.code}
                                    maxChars={128}
                                />
                                {errors.code && (
                                    <div className="invalid-feedback">
                                        {errors.code}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-md-6 ps-md-3 d-flex align-items-start align-self-baseline">
                        <div className="w-100" style={{ minHeight: '85px' }}>
                            <PlanTypeDropdown
                                selectedPlanType={formData.planTypeId}
                                onPlanTypeChange={planTypeId => {
                                    handleFieldFocus();
                                    handlePlanTypeChange(planTypeId);
                                }}
                                inlineLabel={false}
                                isRequired
                                disabled={!!data.id}
                                showTooltip
                                tooltipText={
                                    data.id
                                        ? 'This field cannot be changed. If you need a different type, please create a new benefit plan.'
                                        : 'Once the benefit plan is created, this field cannot be changed.'
                                }
                                errorMessage={
                                    errors.planTypeId ||
                                    (isFieldHighlighted('planTypeId')
                                        ? 'Plan type is required'
                                        : '')
                                }
                                tabIndex={2}
                            />
                        </div>
                    </div>
                    <div className="col-12 col-md-6 pe-md-3 d-flex align-items-start align-self-baseline">
                        <div
                            className="form-group mb-3 w-100"
                            style={{ minHeight: '85px' }}
                        >
                            <label htmlFor="name">
                                {getText('msg_ec0632cbbffa')}{' '}
                                <span className="text-danger">*</span>
                            </label>
                            <div className="position-relative">
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    className={getFieldClasses(
                                        'name',
                                        errors.name,
                                    )}
                                    value={formData.name}
                                    onChange={handleChange}
                                    onFocus={handleFieldFocus}
                                    required
                                    placeholder={getText('msg_64e603ecca7a')}
                                    maxLength={100}
                                    tabIndex={3}
                                />
                                <CharacterLimitIndicator
                                    inputStr={formData.name}
                                    maxChars={100}
                                />
                                {errors.name && (
                                    <div className="invalid-feedback">
                                        {errors.name}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-md-6 ps-md-3 d-flex align-items-start align-self-baseline">
                        <div
                            className="form-group mb-3 w-100"
                            style={{ minHeight: '85px' }}
                        >
                            <label htmlFor="overrideName">
                                {getText('msg_e2e836862394')}
                            </label>
                            <Tooltip
                                html={getText('msg_0ffa9557ae17')}
                                placement="right"
                            >
                                <i
                                    className="bi bi-info-circle"
                                    style={{
                                        paddingLeft: '5px',
                                        fontSize: '13px',
                                        color: 'var(--iNGAGED-Primary-Color)',
                                    }}
                                ></i>
                            </Tooltip>
                            <div className="position-relative">
                                <input
                                    type="text"
                                    id="overrideName"
                                    name="overrideName"
                                    className={getFieldClasses('overrideName')}
                                    value={formData.overrideName}
                                    onChange={handleChange}
                                    onFocus={handleFieldFocus}
                                    placeholder={getText('msg_f4e159020c1a')}
                                    maxLength={100}
                                    tabIndex={4}
                                />
                                <CharacterLimitIndicator
                                    inputStr={formData.overrideName}
                                    maxChars={100}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-md-6 ps-md-3 d-flex align-items-start align-self-baseline">
                        <div
                            className="form-group mb-3 w-100"
                            style={{ minHeight: '85px' }}
                        >
                            <VendorDropdown
                                selectedVendor={formData.vendorId}
                                onVendorChange={vendorId => {
                                    handleFieldFocus();
                                    handleVendorChange(vendorId);
                                }}
                                inlineLabel={false}
                                label={getText('msg_91b0ba24657f')}
                                placeholder={getText('msg_3fb7b9826484')}
                                isRequired
                                errorMessage={
                                    errors.vendorId ||
                                    (isFieldHighlighted('vendorId')
                                        ? 'Carrier is required'
                                        : '')
                                }
                                tabIndex={7}
                            />
                        </div>
                    </div>

                    <div className="col-12 col-md-6 ps-md-3 d-flex align-items-start align-self-baseline">
                        <div
                            className="form-group mb-3 w-100"
                            style={{ minHeight: '85px' }}
                        >
                            <label htmlFor="url">
                                {getText('msg_72ff10feb632')}
                            </label>
                            <div className="position-relative">
                                <input
                                    type="url"
                                    id="url"
                                    name="url"
                                    className={getFieldClasses(
                                        'url',
                                        errors.url &&
                                            (showUrlErrors ||
                                                isFieldHighlighted('url')),
                                    )}
                                    value={formData.url}
                                    onChange={handleChange}
                                    onFocus={handleFieldFocus}
                                    placeholder={getText('msg_80f834edf681')}
                                    maxLength={255}
                                    tabIndex={12}
                                />
                                <CharacterLimitIndicator
                                    inputStr={formData.url}
                                    maxChars={255}
                                />
                                {errors.url &&
                                    (showUrlErrors ||
                                        isFieldHighlighted('url')) && (
                                        <div className="invalid-feedback">
                                            {errors.url}
                                        </div>
                                    )}
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-md-6 pe-md-3 d-flex align-items-start align-self-baseline">
                        <div className="w-100" style={{ minHeight: '85px' }}>
                            <NetworkTypeDropdown
                                selectedNetworkType={formData.networkTypeId}
                                onNetworkTypeChange={handleNetworkTypeChange}
                                inlineLabel={false}
                                label={getText('msg_6fc07c3cae38')}
                                placeholder={getText('msg_9c2755767de2')}
                                tabIndex={5}
                            />
                        </div>
                    </div>

                    <div className="col-12 col-md-6 ps-md-3 d-flex align-items-start align-self-baseline">
                        <div
                            className="form-group mb-3 w-100"
                            style={{ minHeight: '85px' }}
                        >
                            <label htmlFor="networkName">
                                {getText('msg_b1d16c242a46')}
                            </label>
                            <div className="position-relative">
                                <input
                                    type="text"
                                    id="networkName"
                                    name="networkName"
                                    className={getFieldClasses('networkName')}
                                    value={formData.networkName}
                                    onChange={handleChange}
                                    onFocus={handleFieldFocus}
                                    placeholder={getText('msg_d82546fc7f77')}
                                    maxLength={100}
                                    tabIndex={6}
                                />
                                <CharacterLimitIndicator
                                    inputStr={formData.networkName}
                                    maxChars={100}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-md-6 pe-md-3 d-flex align-items-start align-self-baseline">
                        <div
                            className="form-group mb-3 w-100"
                            style={{ minHeight: '85px' }}
                        >
                            <label htmlFor="policy">
                                {getText('msg_bb9cf1418089')}
                            </label>
                            <div className="position-relative">
                                <input
                                    type="text"
                                    id="policy"
                                    name="policy"
                                    className={getFieldClasses('policy')}
                                    value={formData.policy}
                                    onChange={handleChange}
                                    onFocus={handleFieldFocus}
                                    placeholder={getText('msg_4a3a90495178')}
                                    maxLength={100}
                                    tabIndex={8}
                                />
                                <CharacterLimitIndicator
                                    inputStr={formData.policy}
                                    maxChars={100}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-md-6 ps-md-3 d-flex align-items-start align-self-baseline">
                        <div
                            className="form-group mb-3 w-100"
                            style={{ minHeight: '85px' }}
                        >
                            <label htmlFor="providerList">
                                {getText('msg_26a649a2a3e9')}
                            </label>
                            <div className="position-relative">
                                <input
                                    type="url"
                                    id="providerList"
                                    name="providerList"
                                    className={getFieldClasses(
                                        'providerList',
                                        errors.providerList &&
                                            (showUrlErrors ||
                                                isFieldHighlighted(
                                                    'providerList',
                                                )),
                                    )}
                                    value={formData.providerList}
                                    onChange={handleChange}
                                    onFocus={handleFieldFocus}
                                    placeholder={getText('msg_e2d90ab445df')}
                                    maxLength={255}
                                    tabIndex={9}
                                />
                                <CharacterLimitIndicator
                                    inputStr={formData.providerList}
                                    maxChars={255}
                                />
                                {errors.providerList &&
                                    (showUrlErrors ||
                                        isFieldHighlighted('providerList')) && (
                                        <div className="invalid-feedback">
                                            {errors.providerList}
                                        </div>
                                    )}
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-md-6 pe-md-3 d-flex align-items-start align-self-baseline">
                        <div
                            className="form-group mb-3 w-100"
                            style={{ minHeight: '85px' }}
                        >
                            <label htmlFor="phoneNumber">
                                {getText('msg_77064d526523')}
                            </label>
                            <div className="position-relative">
                                <PhoneInput
                                    id="phoneNumber"
                                    className={`form-control ${
                                        errors.phoneNumber ? 'is-invalid' : ''
                                    }`}
                                    value={formData.phoneNumber || ''}
                                    onChange={handlePhoneChange}
                                    onFocus={handleFieldFocus}
                                    name="phoneNumber"
                                    country="US"
                                    placeholder={getText('msg_0925e19cd671')}
                                    tabIndex={10}
                                />
                                {errors.phoneNumber && (
                                    <div className="invalid-feedback">
                                        {errors.phoneNumber}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-12 col-md-6 ps-md-3 d-flex align-items-start align-self-baseline">
                        <div className="w-100" style={{ minHeight: '85px' }}>
                            <SSODropdown
                                selectedSSO={formData.tenantSSOId}
                                onSSOChange={handleSSOChange}
                                inlineLabel={false}
                                label={getText('msg_b859366b8f24')}
                                placeholder={getText('msg_8598222918d3')}
                                client={true}
                                allowNull={true}
                                tabIndex={11}
                            />
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-between mt-4 pt-4 col-12 m-auto">
                    <p>
                        <span className="text-danger">*</span>{' '}
                        {getText('msg_7c118f042eaa')}
                    </p>
                    <button
                        type="button"
                        className="button-base ing-button-primary-left"
                        onClick={onSave}
                    >
                        {getText('msg_efc007a393f6')}
                    </button>
                </div>
            </div>
        );
    },
);

export default BenefitPlanOverviewTab;
